import React, {useState} from 'react'
import useSWRImmutable from 'swr'
import dayjs from 'dayjs'
import {Trans, useTranslation} from 'react-i18next'
import {Badge, Box, Flex, Text} from '@chakra-ui/react'
import {useRails} from '../../../../../contexts/rails'
import {fetcher} from '../../../../../../api'
import {
  camelizeResult,
  dineroMoneyObjects,
} from '../../../../../../api/serializer'

const formatFeeRate = (feeRate) =>
  parseFloat((Number(feeRate) * 100).toFixed(3))

const SupplierRewardsCard = () => {
  const {t} = useTranslation('common')
  const {organisation} = useRails()
  const {data, isLoading} = useSWRImmutable(
    `/api/suppliers/${organisation.currentSupplier.id}/supplier_rewards_summary`,
    fetcher,
    {
      use: [camelizeResult, dineroMoneyObjects],
      revalidateOnFocus: false,
    },
  )

  const [selectedKey, setSelectedKey] = useState(null)

  if (isLoading || !data) {
    return null
  }

  // The API returns tiers ascending by level; list the highest tier first.
  const tiers = [...data.levels].reverse()
  const activeKey = selectedKey || data.currentLevel
  const selectedTier = tiers.find((tier) => tier.key === activeKey)

  return (
    <Box
      className="fs-unmask"
      display="flex"
      flexDirection="column"
      bg="neutral.white"
      p="md"
      my="md"
      border="1px solid"
      borderColor="neutral.glitter"
      borderRadius="lg"
      data-testid="supplier-rewards-card"
    >
      <Flex
        justifyContent="space-between"
        alignItems="center"
        gap="sm"
        flexWrap="wrap"
      >
        <Text
          textStyle="headline5"
          textTransform="uppercase"
          color="mld.neutral.900"
        >
          {t('supplierStatusTiers.title')}
        </Text>
        <Badge variant="neutral">
          <Trans
            t={t}
            i18nKey="supplierStatusTiers.nextReview"
            values={{date: dayjs().add(1, 'month').format('01 MMM YYYY')}}
            components={{b: <Text as="span" fontWeight="bold" />}}
          />
        </Badge>
      </Flex>
      <Text textStyle="headline3" color="mld.neutral.900" mt="md">
        {t('supplierStatusTiers.subtitle')}
      </Text>

      <Box
        mt="md"
        border="0.5px solid"
        borderColor="neutral.900"
        borderRadius="sm"
        overflow="hidden"
      >
        <Flex direction={{base: 'column', md: 'row'}} alignItems="stretch">
          <Box
            flexShrink={0}
            width={{base: '100%', md: '360px'}}
            borderRight={{md: '0.5px solid'}}
            borderColor="neutral.900"
          >
            {tiers.map((tier, index) => {
              const isCurrent = tier.key === data.currentLevel
              const isSelected = tier.key === activeKey
              const isPlatinum = tier.key === 'platinum'
              const isLast = index === tiers.length - 1

              return (
                <Box
                  as="button"
                  type="button"
                  key={tier.key}
                  onClick={() => setSelectedKey(tier.key)}
                  aria-pressed={isSelected}
                  textAlign="left"
                  width="100%"
                  p="md"
                  borderBottom={isLast ? 'none' : '0.5px solid'}
                  borderColor="neutral.900"
                  background={
                    isSelected ? `mld.rewards.${tier.key}` : 'neutral.50'
                  }
                  color={
                    isSelected && !isPlatinum ? 'neutral.white' : 'neutral.1000'
                  }
                >
                  <Text
                    textStyle="caption-medium"
                    textTransform="uppercase"
                    mb="xs"
                    color={
                      isSelected && !isPlatinum
                        ? 'whiteAlpha.900'
                        : 'neutral.800'
                    }
                  >
                    {isCurrent
                      ? t('supplierStatusTiers.currentStatus')
                      : t('supplierStatusTiers.status')}
                  </Text>
                  <Text textStyle="headline3" textTransform="capitalize">
                    {tier.key}
                  </Text>
                </Box>
              )
            })}
          </Box>

          <Box
            flex="1"
            bg="neutral.50"
            pt="md"
            pb="lg"
            px="lg"
            display="flex"
            flexDirection="column"
            justifyContent="flex-end"
          >
            <Text
              textStyle="headline3"
              fontSize="2xl"
              color="mld.neutral.900"
              mb="sm"
            >
              {t('supplierStatusTiers.feeRate', {
                feeRate: formatFeeRate(selectedTier.feeRate),
              })}
            </Text>
            <Text textStyle="body-copy" fontSize="lg" color="neutral.900">
              {t(
                `supplierStatusTiers.supportingCopy.${selectedTier.key}.intro`,
              )}
            </Text>
            <Text
              textStyle="body-copy"
              fontSize="lg"
              color="neutral.900"
              mt="sm"
            >
              {t(
                `supplierStatusTiers.supportingCopy.${selectedTier.key}.detail`,
              )}
            </Text>
          </Box>
        </Flex>
      </Box>
    </Box>
  )
}

export default SupplierRewardsCard
